import React, { useCallback, useEffect, useRef, useState } from "react";
import { AbdmAPI } from "../../services/abdm.service";

const QR_TTL_SECONDS = 15 * 60; // 15 minutes

interface QRData {
  qr_image: string;
  expires_at: string;
}

interface ScanAndShareModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function secondsUntil(isoString: string): number {
  return Math.max(0, Math.floor((new Date(isoString).getTime() - Date.now()) / 1000));
}

function fmtCountdown(secs: number): string {
  const m = Math.floor(secs / 60).toString().padStart(2, "0");
  const s = (secs % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export function ScanAndShareModal({ isOpen, onClose }: ScanAndShareModalProps) {
  const [qr, setQr] = useState<QRData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(QR_TTL_SECONDS);
  const tickRef = useRef<NodeJS.Timeout | null>(null);

  const fetchQR = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await AbdmAPI.generateQR();
      setQr(data);
      setCountdown(secondsUntil(data.expires_at));
    } catch (e: any) {
      setError(e?.message ?? "Failed to generate QR");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on open; cleanup on close
  useEffect(() => {
    if (!isOpen) return;
    fetchQR();
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      setQr(null);
    };
  }, [isOpen, fetchQR]);

  // Countdown timer — auto-refresh when it hits zero
  useEffect(() => {
    if (!qr) return;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          fetchQR();
          return QR_TTL_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [qr, fetchQR]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: "32px 28px",
          width: 420,
          maxWidth: "95vw",
          textAlign: "center",
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 14,
            right: 18,
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 22,
            color: "#94a3b8",
          }}
        >
          ×
        </button>

        <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
          Scan &amp; Share
        </h2>
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "#64748b" }}>
          Patient scans QR with ABHA app to share demographics
        </p>

        {/* QR area */}
        <div
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            padding: 20,
            marginBottom: 16,
            minHeight: 340,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {loading && <p style={{ color: "#94a3b8" }}>Generating QR…</p>}
          {error && <p style={{ color: "#dc2626", fontSize: 13 }}>{error}</p>}
          {qr && !loading && (
            <img
              src={qr.qr_image}
              alt="ABDM Scan & Share QR"
              style={{ width: 300, height: 300, imageRendering: "pixelated" }}
            />
          )}
          {qr && !loading && (
            <p style={{ margin: "12px 0 0", fontSize: 13, color: "#64748b", display: "flex", alignItems: "center", gap: 6 }}>
              🔄 Refreshes in <strong>{fmtCountdown(countdown)}</strong>
            </p>
          )}
        </div>

        <button
          onClick={fetchQR}
          disabled={loading}
          style={{
            width: "100%",
            padding: "9px 0",
            background: loading ? "#e2e8f0" : "#0f172a",
            color: loading ? "#94a3b8" : "#fff",
            border: "none",
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            marginBottom: 20,
          }}
        >
          Refresh QR
        </button>

        {/* Instructions */}
        <div style={{ textAlign: "left", background: "#f8fafc", borderRadius: 10, padding: "14px 16px" }}>
          <p style={{ margin: "0 0 8px", fontWeight: 600, fontSize: 13, color: "#374151" }}>How to use</p>
          <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: "#4b5563", lineHeight: 1.8 }}>
            <li>Open your ABHA app on your phone.</li>
            <li>Tap <strong>Scan QR</strong> and point at this code.</li>
            <li>Confirm sharing your demographics in the app.</li>
            <li>Wait for the receptionist to see your arrival card.</li>
          </ol>
          <p style={{ margin: "10px 0 6px", fontWeight: 600, fontSize: 13, color: "#374151" }}>उपयोग कैसे करें</p>
          <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: "#4b5563", lineHeight: 1.8 }}>
            <li>अपने फ़ोन पर ABHA ऐप खोलें।</li>
            <li><strong>QR स्कैन करें</strong> पर टैप करें और इस कोड पर कैमरा लगाएँ।</li>
            <li>ऐप में अपनी जानकारी साझा करने की पुष्टि करें।</li>
            <li>रिसेप्शनिस्ट को आपका आगमन कार्ड दिखने तक प्रतीक्षा करें।</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
